import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import Button from "shared/components/buttons/Button";
import InputText from "shared/components/inputs/InputText";
import InputSelect from "shared/components/inputs/InputSelect";
import { updateVideoDescriptionAction } from "redux/actions/updateVideoDescriptionAction";
import { removeVideoDescriptionAction } from "redux/actions/removeVideoDescriptionAction";
import "./style.scss";
import Icon from "shared/components/Icon";

export default function TabEdit({ videoDescription, onClose, createMode }) {
    const dispatch = useDispatch();
    const [editableVideoDescription, setEditableVideoDescription] = useState(videoDescription);
    const [videoFile, setVideoFile] = useState(null);
    const [videoSourceType, setVideoSourceType] = useState(videoDescription?.videoUrl ? "URL" : "UPLOAD");

    useEffect(() => {
        setEditableVideoDescription(videoDescription);
        setVideoFile(null);
        setVideoSourceType(videoDescription?.videoUrl ? "URL" : "UPLOAD");
    }, [videoDescription]);

    const onVideoSourceChange = ({ target }) => {
        const nextSourceType = target.value;
        setVideoSourceType(nextSourceType);

        if (nextSourceType === "URL") {
            setVideoFile(null);
            return;
        }

        setEditableVideoDescription(prev => ({
            ...prev,
            videoUrl: ""
        }));
    };

    const onFileChange = (event) => {
        const file = event?.target?.files?.[0];
        setVideoFile(file || null);

        if (file) {
            setEditableVideoDescription(prev => ({
                ...prev,
                videoUrl: ""
            }));
        }
    };

    return editableVideoDescription && (
        <div className="admin-info">
            <div className="tab-info">
                <div className="section-general">
                    <h2>{createMode ? "Create Video Description" : "Edit Video Description"}</h2>
                </div>

                <div className="divider" />

                <InputText
                    name="videoDescriptionTitle"
                    label="Title"
                    value={editableVideoDescription.title || ""}
                    rules={{ required: true }}
                    onChange={({ target }) =>
                        setEditableVideoDescription(prev => ({
                            ...prev,
                            title: target.value
                        }))
                    }
                />

                <InputText
                    name="videoDescriptionDescription"
                    label="Description"
                    value={editableVideoDescription.description || ""}
                    multiline
                    rules={{ required: true }}
                    minRows={5}
                    onChange={({ target }) =>
                        setEditableVideoDescription(prev => ({
                            ...prev,
                            description: target.value
                        }))
                    }
                />

                <InputSelect
                    name="videoDescriptionVideoSource"
                    label="Video Source"
                    value={videoSourceType}
                    onChange={onVideoSourceChange}
                    items={[
                        { value: "URL", label: "Video URL" },
                        { value: "UPLOAD", label: "Upload Video" }
                    ]}
                />

                {videoSourceType === "URL" && (
                    <InputText
                        name="videoDescriptionVideoUrl"
                        label="Video URL"
                        value={editableVideoDescription.videoUrl || ""}
                        onChange={({ target }) =>
                            setEditableVideoDescription(prev => ({
                                ...prev,
                                videoUrl: target.value
                            }))
                        }
                    />
                )}

                {videoSourceType === "UPLOAD" && (
                    <div className="video-upload">
                        <div className="video-file-upload">
                            <label htmlFor="videoUploadInput" className="video-file-label">
                                <input
                                    id="videoUploadInput"
                                    className="video-file-input"
                                    type="file"
                                    accept="video/*"
                                    onChange={onFileChange}
                                />
                                <span className="video-file-cta">
                                    <span className="video-file-icon">
                                        {/* <i className="fas fa-upload" /> */}
                                        <Icon name={"upload"} />
                                    </span>
                                    <span className="video-file-name">
                                        {videoFile ? videoFile.name : "Add Video"}
                                    </span>
                                </span>
                            </label>
                        </div>
                    </div>
                )}

                <InputSelect
                    name="videoDescriptionPublished"
                    label="Publish Status"
                    value={editableVideoDescription.published ? "PUBLISHED" : "DRAFT"}
                    onChange={({ target }) =>
                        setEditableVideoDescription(prev => ({
                            ...prev,
                            published: target.value === "PUBLISHED"
                        }))
                    }
                    items={[
                        { value: "DRAFT", label: "Draft" },
                        { value: "PUBLISHED", label: "Published" }
                    ]}
                />

                <div className="button-container">
                    {!createMode && editableVideoDescription.id && (
                        <Button
                            label="Delete"
                            danger
                            onClick={async () => {
                                const removed = await dispatch(removeVideoDescriptionAction(editableVideoDescription.id));
                                if (removed) {
                                    onClose();
                                }
                            }}
                        />
                    )}
                    <Button
                        label="Save"
                        onClick={() => {
                            dispatch(updateVideoDescriptionAction(editableVideoDescription, videoFile));
                            onClose();
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

TabEdit.propTypes = {
    videoDescription: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    createMode: PropTypes.bool
};
